<template>
  <v-card
    class="template-card"
    :class="{ 'template-card--selected': is_selected }"
    :aria-selected="is_selected"
    :aria-label="is_selected ? `Deselect template` : `Apply template`"
    title="Click to select template"
    tabindex="0"
    role="button"
    :ripple="false"
    flat
    @click="handleClick"
    @keydown.enter.prevent="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <div class="template-preview-area" :class="preview_area_class">
      <div v-if="is_selected" class="selected-indicator" aria-label="Selected template">
        <v-chip small color="primary" text-color="white">
          <v-icon aria-hidden="true" small left>mdi-check</v-icon>
          <strong>{{ chip_label_when_selected }}</strong>
        </v-chip>
      </div>

      <slot name="preview" />
    </div>
  </v-card>
</template>

<script>
/**
 * Controlled Card component to wrap a template preview and allow and visually portray selection state.
 */

export default {
  name: 'TemplateCard',

  props: {
    /* Controlled component - selection state is managed by the parent */
    is_selected: {
      type: Boolean,
      default: false,
    },

    /**
     * Indicates if this template is the only option available
     */
    is_only_option: {
      type: Boolean,
      default: false,
    },

    /* 
      Optional class to apply to the preview area's wrapper for custom styling.
    */
    preview_area_class: {
      type: String,
      default: '',
    },
  },

  computed: {
    chip_label_when_selected() {
      return this.is_only_option
        ? 'Selected'
        : 'Selected - Click to Undo';
    },
  },

  methods: {
    handleClick() {
      this.$emit('select');
    },
  },
};
</script>

<style lang="scss" scoped>
.template-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  z-index: 1;
  position: relative;
  background: transparent;
  border-radius: 0px;
  cursor: pointer;

  &--selected {
    .template-preview-area::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 5px solid var(--v-primary-base);
      border-radius: inherit;
      pointer-events: none;
      z-index: 10;
    }

    &:hover {
      .template-preview-area::before {
        border-color: var(--v-error-base);
      }

      .selected-indicator .v-chip {
        background-color: var(--v-error-base) !important;
      }
    }
  }

  &:before {
    opacity: 0 !important;
  }

  &:focus {
    outline: 2px solid var(--v-primary-base);
    outline-offset: 0;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }
}

.template-preview-area {
  flex-grow: 1;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.selected-indicator {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 20;
  pointer-events: none;
  display: flex;
  align-items: center;

  .v-chip {
    transition: all 0.2s ease;
  }
}
</style>
